const MAX_PHOTO_BYTES = 300_000;

const toBase64 = (buf: ArrayBuffer): string => {
  let s = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
};

/**
 * Turns a picked photo into something the server accepts: scaled down in the browser (a phone photo can be many
 * megabytes) and saved as a JPEG, trying smaller sizes and lower quality until it is small enough.
 */
export async function preparePhoto(file: File): Promise<string> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error('Choose a JPEG, PNG or WebP picture.');
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('That picture could not be opened.');
  });
  for (const [side, quality] of [[640, 0.85], [640, 0.7], [480, 0.7], [360, 0.6]] as const) {
    const scale = Math.min(1, side / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff'; // a transparent PNG would turn black as a JPEG
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', quality));
    if (blob && blob.size <= MAX_PHOTO_BYTES) return toBase64(await blob.arrayBuffer());
  }
  throw new Error('That picture is too detailed. Try a simpler or smaller one.');
}

/** Where an item's picture is served from; `version` changes with each upload so a new picture shows at once. */
export const menuPhotoUrl = (id: string, version: number): string => `/api/public/menu-photo/${id}?v=${version}`;
